import { useEffect, useState } from 'react'

import { Link } from 'react-router-dom'
import { AgGridReact } from 'ag-grid-react'
import {
  ColDef,
  ValueFormatterParams,
  ICellRendererParams,
} from 'ag-grid-community'

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'

import { BuyerProposalOverviewDto } from '../../shared/types'
import { ProposalsApi } from '../../../api/proposals-api'
import { Box, Button, useTheme } from '@mui/material'
import GicBreadcrumbs, { BreadcrumbItem } from '../../shared/breadcrumbs.tsx'
import { Link as RouterLink } from 'react-router'
import { HEADER_HEIGHT_PX } from '../../shared/constants.ts'
import {
  formatCurrency,
  formatDate,
  formatDateTime,
} from '../../shared/formats.ts'

import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'

ModuleRegistry.registerModules([AllCommunityModule])

export default function ProposalOverviewPage() {
  const theme = useTheme()
  const [rowData, setRowData] = useState<BuyerProposalOverviewDto[]>([])

  useEffect(() => {
    void (async () => {
      const data = await ProposalsApi.getBuyerProposals()
      setRowData(data)
    })()
  }, [])

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Proposals', path: '/proposals' },
  ]

  const columnDefs: ColDef<BuyerProposalOverviewDto>[] = [
    { headerName: 'Fund', field: 'fund', flex: 1 },
    {
      headerName: 'Bid Deadline',
      field: 'bidDeadline',
      valueFormatter: (
        params: ValueFormatterParams<
          BuyerProposalOverviewDto,
          BuyerProposalOverviewDto['bidDeadline']
        >
      ) => formatDateTime(params.value),
      flex: 1.5,
    },
    { headerName: 'Status', field: 'status', flex: 1 },
    {
      headerName: 'Responses',
      field: 'responseSummary',
      flex: 1,
    },
    {
      headerName: 'Deposit Amount',
      field: 'depositAmount',
      valueFormatter: (
        params: ValueFormatterParams<
          BuyerProposalOverviewDto,
          BuyerProposalOverviewDto['depositAmount']
        >
      ) => formatCurrency(params.value),
      cellStyle: { textAlign: 'right' },
      flex: 1,
    },
    {
      headerName: 'Deposit Date',
      field: 'depositDate',
      valueFormatter: (
        params: ValueFormatterParams<
          BuyerProposalOverviewDto,
          BuyerProposalOverviewDto['depositDate']
        >
      ) => formatDate(params.value),
      flex: 1,
    },
    {
      headerName: 'Created At',
      field: 'createdAt',
      valueFormatter: (
        params: ValueFormatterParams<
          BuyerProposalOverviewDto,
          BuyerProposalOverviewDto['createdAt']
        >
      ) => formatDateTime(params.value),
      flex: 1,
      sort: 'desc',
    },
    {
      headerName: 'Details',
      cellRenderer: (params: ICellRendererParams<BuyerProposalOverviewDto>) => {
        if (!params.data) return null
        const proposalId = params.data.proposalId
        return <Link to={`/proposals/item/${proposalId}`}>Details</Link>
      },
      flex: 0.5,
    },
  ]

  return (
    <Box
      sx={{
        padding: 3,
        paddingTop: 1,
        overflow: 'auto',
        boxSizing: 'border-box',
        minHeight: `calc(100vh - ${HEADER_HEIGHT_PX}px)`, // Full height minus header
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <GicBreadcrumbs items={breadcrumbItems} />
        <Button
          style={{ height: '30px', marginBottom: 'auto', marginTop: 'auto' }}
          component={RouterLink}
          to="/proposals/new"
          color="primary"
          variant="outlined"
          size="small"
        >
          Create Proposal
        </Button>
      </div>

      <Box
        className="ag-theme-quartz-auto-dark ag-theme-cwan compact-grid"
        sx={{
          height: `calc(100vh - ${HEADER_HEIGHT_PX + 75}px)`,
          width: '100%',
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <AgGridReact<BuyerProposalOverviewDto>
          theme="legacy" // Use legacy theming for v32 CSS compatibility
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
          }}
        />
      </Box>
    </Box>
  )
}
